import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

interface Props {
  urls: string[]
  startIndex?: number
  onClose: () => void
}

export default function ImageViewer({ urls, startIndex = 0, onClose }: Props) {
  const [current, setCurrent] = useState(startIndex)

  const prev = () => setCurrent(i => (i === 0 ? urls.length - 1 : i - 1))
  const next = () => setCurrent(i => (i === urls.length - 1 ? 0 : i + 1))

  return (
    <motion.div
      className="fixed inset-0 z-[100] bg-black flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-4 safe-top">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"
        >
          <X size={20} className="text-white" />
        </button>
        {urls.length > 1 && (
          <span className="font-mono text-xs text-white/50">
            {current + 1} / {urls.length}
          </span>
        )}
        <div className="w-10" />
      </div>

      {/* Image */}
      <div className="flex-1 flex items-center justify-center relative px-2">
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            src={urls[current]}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg select-none"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            draggable={false}
          />
        </AnimatePresence>

        {/* Prev / Next arrows */}
        {urls.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center"
            >
              <ChevronLeft size={22} className="text-white" />
            </button>
            <button
              onClick={next}
              className="absolute right-3 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center"
            >
              <ChevronRight size={22} className="text-white" />
            </button>
          </>
        )}
      </div>

      {/* Dot indicators */}
      {urls.length > 1 && (
        <div className="flex justify-center gap-1.5 py-5 safe-bottom">
          {urls.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`rounded-full transition-all duration-200
                ${i === current ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/30'}`}
            />
          ))}
        </div>
      )}
    </motion.div>
  )
}
